import { useState } from "react";
import { Search, Plus, Sparkles, BookOpen, Info, Sparkles as SparklesIcon } from "lucide-react";

type QuoteSourceType = "excerpt" | "echo" | "original";

interface Quote {
  id: string;
  content: string;
  source: string;
  sourceType: QuoteSourceType;
  sourceDetail?: string;
  tags: { emoji: string; name: string }[];
  appliedCount: number;
  topic: string;
  explanation?: string;
  scenarios?: string[];
  similar?: { content: string; source: string }[];
  bookRecommendation?: string;
}

interface Topic {
  emoji: string;
  name: string;
  count: number;
  color: string;
  rotation: number;
  x: number;
  y: number;
  relatedTopics: string[];
  compact?: boolean;
}

interface AppliedRecord {
  id: string;
  date: string;
  content: string;
  context: string;
}

const topics: Topic[] = [
  { emoji: "💬", name: "高情商回应", count: 24, color: "rgba(168, 230, 207, 0.18)", rotation: 2, x: 52, y: 22, relatedTopics: ["安慰别人", "回应情绪", "共情", "陪伴"] },
  { emoji: "🌸", name: "幽默接梗", count: 20, color: "rgba(255, 243, 216, 0.15)", rotation: -3, x: 158, y: 26, relatedTopics: ["化解尴尬", "破冰"] },
  { emoji: "🌱", name: "安慰别人", count: 18, color: "rgba(168, 230, 207, 0.15)", rotation: 1, x: 14, y: 65, relatedTopics: ["高情商回应", "共情", "陪伴", "真诚表达"] },
  { emoji: "✨", name: "描述风景", count: 16, color: "rgba(242, 234, 255, 0.15)", rotation: -2, x: 198, y: 68, relatedTopics: ["描述感受", "观察世界", "旅行"] },
  { emoji: "💼", name: "职场表达", count: 15, color: "rgba(234, 244, 255, 0.18)", rotation: 3, x: 125, y: 105, relatedTopics: ["逻辑表达", "演讲表达", "向上沟通"] },
  { emoji: "💝", name: "表达感谢", count: 12, color: "rgba(255, 243, 247, 0.15)", rotation: -1, x: 22, y: 96, relatedTopics: ["真诚表达", "共情"] },
  { emoji: "🎯", name: "初次见面", count: 10, color: "rgba(255, 243, 216, 0.12)", rotation: 2, x: 225, y: 96, relatedTopics: ["自我介绍", "职场表达"] },
  { emoji: "🌊", name: "描述感受", count: 9, color: "rgba(242, 234, 255, 0.12)", rotation: -3, x: 18, y: 162, relatedTopics: ["描述风景", "情绪表达"], compact: true },
  { emoji: "🎙️", name: "演讲表达", count: 9, color: "rgba(200, 240, 245, 0.15)", rotation: 1, x: 198, y: 163, relatedTopics: ["逻辑表达", "职场表达"] },
  { emoji: "❤️", name: "亲密关系", count: 8, color: "rgba(255, 243, 247, 0.15)", rotation: -2, x: 35, y: 198, relatedTopics: ["共情", "真诚表达"], compact: true },
  { emoji: "🍃", name: "拒绝别人", count: 7, color: "rgba(168, 230, 207, 0.12)", rotation: 3, x: 170, y: 198, relatedTopics: ["边界感", "职场表达"] },
  { emoji: "🌙", name: "自我介绍", count: 7, color: "rgba(255, 243, 216, 0.12)", rotation: -1, x: 235, y: 130, relatedTopics: ["初次见面", "真诚表达"] },
  { emoji: "🎭", name: "化解尴尬", count: 6, color: "rgba(255, 243, 216, 0.12)", rotation: 2, x: 95, y: 230, relatedTopics: ["幽默接梗", "自嘲"], compact: true },
  { emoji: "🧠", name: "底层逻辑", count: 6, color: "rgba(200, 240, 245, 0.12)", rotation: -3, x: 115, y: 165, relatedTopics: ["逻辑表达", "演讲表达"] },
];

const quotes: Quote[] = [
  {
    id: "1",
    content: "人和人的相遇，本来就是一场彼此照亮。",
    source: "《云边有个小卖部》",
    sourceType: "excerpt",
    sourceDetail: "作者：张嘉佳 · 2018",
    tags: [
      { emoji: "🌿", name: "安慰别人" },
      { emoji: "💬", name: "陪伴" },
      { emoji: "✨", name: "温柔表达" },
    ],
    appliedCount: 3,
    topic: "安慰别人",
    explanation: "它没有直接讲大道理，而是借「照亮」这个意象，把人与人的陪伴表达得更加具体，也更容易引起情感共鸣。",
    scenarios: ["安慰朋友", "久别重逢", "毕业留言", "写给未来的自己"],
    similar: [
      { content: "有人与你立黄昏。", source: "网络" },
      { content: "总有人间一两风，填我十万八千梦。", source: "网络" },
      { content: "陪伴，本身就是一种回答。", source: "《岛上书店》" },
    ],
    bookRecommendation: "这句话出自小说《云边有个小卖部》，整本书围绕「陪伴、遗憾与成长」展开。如果你喜欢这种温柔而克制的表达，也许还会喜欢《岛上书店》《解忧杂货店》等作品。",
  },
  {
    id: "2",
    content: "陪伴，本身就是一种回答。",
    source: "《岛上书店》",
    sourceType: "excerpt",
    sourceDetail: "作者：加·泽文 · 2014",
    tags: [
      { emoji: "🌱", name: "共情" },
      { emoji: "💬", name: "倾听" },
      { emoji: "❤️", name: "亲密关系" },
    ],
    appliedCount: 2,
    topic: "安慰别人",
    explanation: "它把抽象的「陪伴」和具体的「回答」联系起来，让人意识到：有时候什么都不用说，陪在身边就是最好的回应。",
    scenarios: ["朋友难过时", "家人需要时", "默默支持"],
  },
  {
    id: "3",
    content: "有些难过，不需要马上变好。",
    source: "Echo 练习后收录",
    sourceType: "echo",
    sourceDetail: "来自一次陪练后的表达优化",
    tags: [
      { emoji: "🌱", name: "回应情绪" },
      { emoji: "💬", name: "共情" },
      { emoji: "✨", name: "用户原创" },
    ],
    appliedCount: 5,
    topic: "高情商回应",
    explanation: "这句话的力量在于它接纳了情绪的存在，而不是急着给出解决方案。有时候，允许自己难过，本身就是一种治愈。",
    scenarios: ["安慰朋友", "自我对话", "情绪管理"],
  },
  {
    id: "4",
    content: "把问题讲清楚，比急着证明自己更重要。",
    source: "《金字塔原理》",
    sourceType: "excerpt",
    sourceDetail: "作者：芭芭拉·明托",
    tags: [
      { emoji: "💼", name: "向上沟通" },
      { emoji: "🧠", name: "逻辑表达" },
    ],
    appliedCount: 1,
    topic: "职场表达",
    explanation: "它提醒我们，沟通的目的是解决问题，而不是赢得争论。先把问题说清楚，价值自然会被看见。",
    scenarios: ["汇报工作", "项目讨论", "解决冲突"],
  },
  {
    id: "5",
    content: "我们讨论的是问题，不是彼此。",
    source: "Echo 精炼",
    sourceType: "echo",
    sourceDetail: "来自一次会议表达优化",
    tags: [
      { emoji: "💼", name: "冲突沟通" },
      { emoji: "💬", name: "会议表达" },
    ],
    appliedCount: 4,
    topic: "职场表达",
    explanation: "这句话帮我们在冲突中划定边界——攻击问题，而不是攻击人。这是成熟沟通的重要标志。",
    scenarios: ["会议争论", "观点分歧", "团队沟通"],
  },
  {
    id: "6",
    content: "我理解你的想法，但我更担心的是……",
    source: "Echo 实战优化",
    sourceType: "echo",
    sourceDetail: "来自一次谈判练习",
    tags: [
      { emoji: "💬", name: "高情商回应" },
      { emoji: "✨", name: "表达观点" },
    ],
    appliedCount: 3,
    topic: "职场表达",
    explanation: "它先认同对方，再表达自己的立场，既保留了对方的面子，又清晰地传达了自己的观点。",
    scenarios: ["客户谈判", "团队讨论", "跨部门沟通"],
  },
  {
    id: "7",
    content: "海风没有说话，却把白天吹得越来越轻。",
    source: "用户练习收录",
    sourceType: "original",
    sourceDetail: "收录于 6 月 13 日 · 「观察」练习",
    tags: [
      { emoji: "🌊", name: "旅行" },
      { emoji: "✨", name: "观察世界" },
    ],
    appliedCount: 2,
    topic: "描述风景",
    explanation: "它用拟人的手法，把无形的「风」和抽象的「白天」联系起来，创造了一种轻盈而安静的画面感。",
    scenarios: ["旅行记录", "朋友圈配文", "写作练习"],
  },
  {
    id: "8",
    content: "原来夕阳不是落下去了，而是慢慢沉进了海里。",
    source: "Echo 推荐表达",
    sourceType: "echo",
    sourceDetail: "来自一次观察练习",
    tags: [
      { emoji: "🌊", name: "自然描写" },
      { emoji: "✨", name: "画面感" },
    ],
    appliedCount: 4,
    topic: "描述风景",
    explanation: "它把「落下」这个普通的动作，转化为「沉进海里」这个更有想象力的画面，让整个场景更加生动。",
    scenarios: ["旅行记录", "日记写作", "诗歌创作"],
  },
  {
    id: "9",
    content: "风把树叶翻了一页，秋天就开始了。",
    source: "摘录",
    sourceType: "excerpt",
    sourceDetail: "网络",
    tags: [
      { emoji: "🍂", name: "四季" },
      { emoji: "✨", name: "细节观察" },
    ],
    appliedCount: 1,
    topic: "描述风景",
    explanation: "它用一个微小的细节——风吹动树叶——来暗示季节的变化，让整个表达更加细腻动人。",
    scenarios: ["季节描写", "生活记录", "散文写作"],
  },
  {
    id: "10",
    content: "世界终于比手机屏幕大了一点。",
    source: "用户旅行练习",
    sourceType: "original",
    sourceDetail: "收录于 5 月 28 日 · 「感受」练习",
    tags: [
      { emoji: "🌊", name: "成长" },
      { emoji: "✨", name: "旅行" },
    ],
    appliedCount: 6,
    topic: "描述感受",
    explanation: "它用对比的方式，把「世界」和「手机屏幕」放在一起，精准表达了现代人从数字世界回归现实的顿悟时刻。",
    scenarios: ["旅行感悟", "生活记录", "告别焦虑"],
  },
  {
    id: "11",
    content: "那一刻，我忽然慢了下来。",
    source: "Echo 陪练生成",
    sourceType: "echo",
    sourceDetail: "来自一次感受练习",
    tags: [
      { emoji: "🌱", name: "生活记录" },
    ],
    appliedCount: 2,
    topic: "描述感受",
    explanation: "它用简单的语言，捕捉了一个人从忙碌到平静的瞬间转变，让读者能够感受到那种内心的变化。",
    scenarios: ["生活记录", "心情日记", "冥想感悟"],
  },
  {
    id: "12",
    content: "原来开心的时候，人真的会忘记时间。",
    source: "用户原创",
    sourceType: "original",
    sourceDetail: "收录于 7 月 2 日 · 「感受」练习",
    tags: [
      { emoji: "✨", name: "情绪表达" },
    ],
    appliedCount: 3,
    topic: "描述感受",
    explanation: "它用「忘记时间」这个具体的体验，来表达「开心」这种抽象的情绪，让表达更加真实可感。",
    scenarios: ["心情分享", "日记写作", "朋友聊天"],
  },
  {
    id: "13",
    content: "听起来，你最近真的很辛苦。",
    source: "Echo 陪练",
    sourceType: "echo",
    sourceDetail: "来自一次共情练习",
    tags: [
      { emoji: "💬", name: "回应情绪" },
    ],
    appliedCount: 8,
    topic: "高情商回应",
    explanation: "这句话没有急着安慰，而是先确认对方的感受。有时候，被看见比被安慰更重要。",
    scenarios: ["朋友倾诉", "同事吐槽", "家人抱怨"],
  },
  {
    id: "14",
    content: "谢谢你告诉我这些。",
    source: "用户收藏",
    sourceType: "excerpt",
    sourceDetail: "网络",
    tags: [
      { emoji: "💬", name: "倾听" },
    ],
    appliedCount: 5,
    topic: "高情商回应",
    explanation: "它用简单的一句话，表达了对对方分享的感谢和尊重，让对方感受到被重视。",
    scenarios: ["朋友分享", "同事沟通", "亲密关系"],
  },
  {
    id: "15",
    content: "如果今天很累，那今天就先休息。",
    source: "Echo 推荐",
    sourceType: "echo",
    sourceDetail: "来自一次自我关怀练习",
    tags: [
      { emoji: "🌱", name: "安慰朋友" },
    ],
    appliedCount: 4,
    topic: "高情商回应",
    explanation: "它允许对方「累」，而不是要求对方「加油」，这种接纳的态度本身就是一种治愈。",
    scenarios: ["安慰朋友", "自我对话", "同事关怀"],
  },
  {
    id: "16",
    content: "我很想帮你，但这次可能做不到。",
    source: "Echo",
    sourceType: "echo",
    sourceDetail: "来自一次边界感练习",
    tags: [
      { emoji: "🍃", name: "边界感" },
    ],
    appliedCount: 6,
    topic: "拒绝别人",
    explanation: "它先表达善意，再说明无法帮助的事实，既维护了自己的边界，又保留了对方的感受。",
    scenarios: ["拒绝请求", "职场沟通", "朋友求助"],
  },
  {
    id: "17",
    content: "这件事我暂时没有办法答应，不过如果以后有机会……",
    source: "实战训练",
    sourceType: "echo",
    sourceDetail: "来自一次职场拒绝练习",
    tags: [
      { emoji: "💼", name: "职场" },
    ],
    appliedCount: 2,
    topic: "拒绝别人",
    explanation: "它用「暂时」和「以后有机会」来软化拒绝，既明确了态度，又为未来的合作留下了可能性。",
    scenarios: ["职场拒绝", "客户沟通", "团队协作"],
  },
  {
    id: "18",
    content: "谢谢你想到我，不过这次我想把时间留给自己。",
    source: "用户原创",
    sourceType: "original",
    sourceDetail: "收录于 6 月 20 日 · 「拒绝」练习",
    tags: [
      { emoji: "🌱", name: "生活" },
    ],
    appliedCount: 3,
    topic: "拒绝别人",
    explanation: "它先感谢对方的好意，再表达自己的需求，让拒绝变得更加温和而坚定。",
    scenarios: ["生活拒绝", "社交场合", "朋友邀约"],
  },
  {
    id: "19",
    content: "哈哈，我们先别急着社死。",
    source: "用户收藏",
    sourceType: "excerpt",
    sourceDetail: "网络",
    tags: [
      { emoji: "🎭", name: "幽默" },
    ],
    appliedCount: 4,
    topic: "化解尴尬",
    explanation: "它用幽默的方式调侃尴尬场面，让紧张的氛围变得轻松起来。",
    scenarios: ["尴尬时刻", "社交场合", "朋友聚会"],
  },
  {
    id: "20",
    content: "看来今天最紧张的人不是只有我。",
    source: "Echo",
    sourceType: "echo",
    sourceDetail: "来自一次破冰练习",
    tags: [
      { emoji: "🎭", name: "破冰" },
    ],
    appliedCount: 5,
    topic: "化解尴尬",
    explanation: "它用自嘲的方式拉近距离，让对方意识到「原来不止我一个人紧张」，从而放松下来。",
    scenarios: ["初次见面", "会议开场", "演讲前"],
  },
  {
    id: "21",
    content: "这件事以后应该会变成很好笑的故事。",
    source: "用户练习",
    sourceType: "original",
    sourceDetail: "收录于 5 月 15 日 · 「化解尴尬」练习",
    tags: [
      { emoji: "🎭", name: "自嘲" },
    ],
    appliedCount: 2,
    topic: "化解尴尬",
    explanation: "它用「未来视角」来看待当下的尴尬，让眼前的困境变得不那么重要。",
    scenarios: ["尴尬时刻", "失误道歉", "社交失误"],
  },
  {
    id: "22",
    content: "我不是想赢这次争论，我只是想让你知道我的感受。",
    source: "Echo 推荐",
    sourceType: "echo",
    sourceDetail: "来自一次亲密关系练习",
    tags: [
      { emoji: "❤️", name: "情侣沟通" },
    ],
    appliedCount: 3,
    topic: "亲密关系",
    explanation: "它把「争论」转化为「表达感受」，让对方意识到这不是一场战争，而是一次沟通。",
    scenarios: ["情侣吵架", "家庭沟通", "亲密关系"],
  },
  {
    id: "23",
    content: "谢谢你愿意听我说这些。",
    source: "用户收藏",
    sourceType: "excerpt",
    sourceDetail: "网络",
    tags: [
      { emoji: "❤️", name: "真诚" },
    ],
    appliedCount: 4,
    topic: "亲密关系",
    explanation: "它用简单的一句话，表达了对对方倾听的感激，让对方感受到自己的价值。",
    scenarios: ["朋友聊天", "情侣沟通", "家人交流"],
  },
  {
    id: "24",
    content: "慢一点也没关系，我们可以一起长大。",
    source: "Echo",
    sourceType: "echo",
    sourceDetail: "来自一次成长练习",
    tags: [
      { emoji: "❤️", name: "成长" },
    ],
    appliedCount: 2,
    topic: "亲密关系",
    explanation: "它用「一起长大」来表达陪伴的意义，让对方感受到安全感和共同成长的力量。",
    scenarios: ["情侣沟通", "朋友鼓励", "亲子关系"],
  },
  {
    id: "25",
    content: "这个梗我先接住，回头再还你一个。",
    source: "用户收藏",
    sourceType: "excerpt",
    sourceDetail: "网络",
    tags: [
      { emoji: "🌸", name: "聊天" },
    ],
    appliedCount: 3,
    topic: "幽默接梗",
    explanation: "它用俏皮的方式回应对方的玩笑，既接住了梗，又为后续的互动留下了空间。",
    scenarios: ["朋友聊天", "社交媒体", "轻松场合"],
  },
  {
    id: "26",
    content: "生活不给我剧本，我只能临场发挥。",
    source: "Echo",
    sourceType: "echo",
    sourceDetail: "来自一次幽默练习",
    tags: [
      { emoji: "🌸", name: "幽默" },
    ],
    appliedCount: 4,
    topic: "幽默接梗",
    explanation: "它用比喻的方式调侃生活的不确定性，让严肃的话题变得轻松有趣。",
    scenarios: ["朋友聊天", "社交媒体", "演讲开场"],
  },
  {
    id: "27",
    content: "尴尬的时候，先笑一下，世界就没那么认真了。",
    source: "用户练习",
    sourceType: "original",
    sourceDetail: "收录于 6 月 8 日 · 「幽默」练习",
    tags: [
      { emoji: "🌸", name: "破冰" },
    ],
    appliedCount: 2,
    topic: "幽默接梗",
    explanation: "它用「笑一下」这个简单的动作，来化解尴尬，让周围的氛围变得轻松起来。",
    scenarios: ["尴尬时刻", "社交场合", "朋友聚会"],
  },
  {
    id: "28",
    content: "观点可以不同，但事实最好一致。",
    source: "Echo",
    sourceType: "echo",
    sourceDetail: "来自一次逻辑练习",
    tags: [
      { emoji: "🧠", name: "逻辑" },
    ],
    appliedCount: 3,
    topic: "底层逻辑",
    explanation: "它区分了「观点」和「事实」，让讨论更加理性和有效。",
    scenarios: ["辩论", "会议讨论", "观点交流"],
  },
  {
    id: "29",
    content: "比结论更重要的是推导过程。",
    source: "摘录",
    sourceType: "excerpt",
    sourceDetail: "网络",
    tags: [
      { emoji: "🧠", name: "思辨" },
    ],
    appliedCount: 1,
    topic: "底层逻辑",
    explanation: "它强调思考过程的重要性，让对方理解你的思路，而不仅仅是接受你的结论。",
    scenarios: ["汇报工作", "学术讨论", "观点表达"],
  },
  {
    id: "30",
    content: "真正厉害的人，会先定义问题。",
    source: "Echo 推荐",
    sourceType: "echo",
    sourceDetail: "来自一次深度思考练习",
    tags: [
      { emoji: "🧠", name: "思考" },
    ],
    appliedCount: 2,
    topic: "底层逻辑",
    explanation: "它提醒我们，在解决问题之前，先要搞清楚问题到底是什么。",
    scenarios: ["问题分析", "项目规划", "决策制定"],
  },
  {
    id: "31",
    content: "一个故事，比十个观点更容易被记住。",
    source: "摘录",
    sourceType: "excerpt",
    sourceDetail: "网络",
    tags: [
      { emoji: "🎙️", name: "演讲" },
    ],
    appliedCount: 3,
    topic: "演讲表达",
    explanation: "它用对比的方式，强调故事在表达中的重要性，让你的表达更加生动有力。",
    scenarios: ["演讲", "汇报", "分享会"],
  },
  {
    id: "32",
    content: "先让别人愿意听，再让别人愿意相信。",
    source: "Echo",
    sourceType: "echo",
    sourceDetail: "来自一次表达结构练习",
    tags: [
      { emoji: "🎙️", name: "表达结构" },
    ],
    appliedCount: 2,
    topic: "演讲表达",
    explanation: "它揭示了表达的两个层次：吸引注意力和建立信任，让你的表达更加有效。",
    scenarios: ["演讲", "说服", "销售"],
  },
  {
    id: "33",
    content: "停顿，也是表达的一部分。",
    source: "用户练习",
    sourceType: "original",
    sourceDetail: "收录于 7 月 5 日 · 「演讲」练习",
    tags: [
      { emoji: "🎙️", name: "口语" },
    ],
    appliedCount: 1,
    topic: "演讲表达",
    explanation: "它提醒我们，不要急于说完，适当的停顿可以让听众更好地理解和消化你的内容。",
    scenarios: ["演讲", "面试", "重要场合"],
  },
];

const appliedRecords: AppliedRecord[] = [
  {
    id: "1",
    date: "昨天",
    content: "陪伴，本身就是一种回答。",
    context: "在安慰朋友时自然化用了",
  },
  {
    id: "2",
    date: "三天前",
    content: "世界终于比手机屏幕大了一点。",
    context: "在旅行照片配文中化用了",
  },
  {
    id: "3",
    date: "一周前",
    content: "人和人的相遇，本来就是一场彼此照亮。",
    context: "在毕业留言中化用了",
  },
];

export default function ArsenalPage() {
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [flippedCard, setFlippedCard] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);

  const filteredQuotes = selectedTopic
    ? quotes.filter((q) => q.topic === selectedTopic)
    : quotes;

  const handleUseQuote = (quote: Quote) => {
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  };

  const getSourceBadge = (type: QuoteSourceType) => {
    switch (type) {
      case "excerpt":
        return { emoji: "📖", text: "摘录", color: "rgba(234, 244, 255, 0.3)" };
      case "echo":
        return { emoji: "✨", text: "Echo", color: "rgba(242, 234, 255, 0.3)" };
      case "original":
        return { emoji: "🌱", text: "我的表达", color: "rgba(221, 246, 232, 0.3)" };
      default:
        return { emoji: "📖", text: "摘录", color: "rgba(234, 244, 255, 0.3)" };
    }
  };

  return (
    <div className="relative min-h-full">
      <div className="glow-orb" style={{ width: 260, height: 260, background: "#FFF3D8", top: -70, right: -90, opacity: 0.55 }} />
      <div className="glow-orb" style={{ width: 220, height: 220, background: "#F2EAFF", top: 340, left: -70, opacity: 0.38 }} />
      <div className="glow-orb" style={{ width: 200, height: 200, background: "#FFF3F7", top: 630, right: -60, opacity: 0.32 }} />
      <div className="glow-orb" style={{ width: 180, height: 180, background: "#FEF9E7", top: 500, left: 10, opacity: 0.28 }} />

      {/* Header */}
      <header className="relative px-5 pt-5 pb-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-[24px] font-bold text-echo-text leading-tight">📚 弹药库</h1>
            <p className="text-echo-text/45 text-[13px] mt-1">收录一句话，直到它变成你的话。</p>
          </div>
          <div className="flex items-center gap-2">
            <button className="icon-btn">
              <Search size={17} strokeWidth={2} className="text-echo-text/70" />
            </button>
            <button className="w-9 h-9 rounded-full flex items-center justify-center animate-spring" style={{ background: "linear-gradient(135deg, #FFF3D8, #F2EAFF)" }}>
              <Plus size={17} strokeWidth={2} className="text-echo-text" />
            </button>
          </div>
        </div>
      </header>

      {/* Expression Map - Floating Nebula */}
      <section className="px-5 pb-5">
        <div className="mb-4">
          <h2 className="text-[17px] font-bold text-echo-text flex items-center gap-2">
            <span>🌌</span>
            表达图谱
          </h2>
          <p className="text-echo-text/45 text-[13px] mt-1">
            AI 正在慢慢理解，你最擅长怎样表达。
          </p>
        </div>

        <div className="relative glass-card rounded-card mx-auto" style={{ minHeight: 260, width: "92%", padding: "24px 0" }}>
          {selectedTopic && (
            <svg className="absolute inset-0 w-full h-full pointer-events-none">
              {topics.map((topic) => {
                if (topic.name === selectedTopic) return null;
                if (topics.find(t => t.name === selectedTopic)?.relatedTopics.includes(topic.name)) {
                  const selected = topics.find(t => t.name === selectedTopic)!;
                  return (
                    <line
                      key={`${selectedTopic}-${topic.name}`}
                      x1={selected.x + 60}
                      y1={selected.y + 20}
                      x2={topic.x + 50}
                      y2={topic.y + 18}
                      stroke="rgba(168, 230, 207, 0.25)"
                      strokeWidth="1"
                      strokeDasharray="4"
                      className="animate-pulse"
                    />
                  );
                }
                return null;
              })}
            </svg>
          )}

          {topics.map((topic, i) => {
            const isSelected = selectedTopic === topic.name;
            const isRelated = selectedTopic && topics.find(t => t.name === selectedTopic)?.relatedTopics.includes(topic.name);
            const opacity = selectedTopic ? (isSelected ? 1 : isRelated ? 0.7 : 0.25) : 1;
            const scale = isSelected ? 1.15 : isRelated ? 1.05 : 1;
            const fontSize = topic.count > 20 ? "text-[15px]" : topic.count > 15 ? "text-[14px]" : topic.count > 10 ? "text-[13px]" : "text-[12px]";
            const paddingX = topic.compact ? "px-2" : topic.count > 20 ? "px-5" : topic.count > 15 ? "px-4" : topic.count > 10 ? "px-3.5" : "px-3";
            const paddingY = topic.compact ? "py-2.5" : topic.count > 20 ? "py-2.5" : topic.count > 15 ? "py-2" : "py-1.5";

            return (
              <button
                key={i}
                onClick={() => setSelectedTopic(isSelected ? null : topic.name)}
                className={`absolute flex items-center gap-1.5 rounded-full font-medium transition-all duration-300 backdrop-blur-xl border border-white/30 ${paddingX} ${paddingY} ${fontSize}`}
                style={{
                  left: topic.x,
                  top: topic.y,
                  transform: `rotate(${topic.rotation}deg) scale(${scale})`,
                  opacity: opacity,
                  background: isSelected ? `${topic.color}dd` : `${topic.color}80`,
                  color: isSelected ? "#2E2A39" : "rgba(46, 42, 57, 0.7)",
                  boxShadow: isSelected ? "0 8px 24px rgba(168, 230, 207, 0.2)" : "0 4px 12px rgba(0, 0, 0, 0.04)",
                  animationDelay: `${i * 0.15}s`,
                  animation: "float 4s ease-in-out infinite",
                }}
              >
                <span>{topic.emoji}</span>
                <span>{topic.name}</span>
                {!topic.compact && <span className="text-[11px] opacity-50">({topic.count})</span>}
              </button>
            );
          })}
        </div>

        <div className="mt-4 px-4 py-3 rounded-full bg-[#F2EAFF]/15 flex items-center gap-2">
          <Sparkles size={14} strokeWidth={2} className="text-[#8B5CF6]" />
          <span className="text-[12px] text-echo-text/50">点击标签，探索你的表达网络</span>
        </div>
      </section>

      {/* Quote Cards */}
      <section className="px-5 pb-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[17px] font-bold text-echo-text">
            {selectedTopic ? `${selectedTopic} · ${filteredQuotes.length} 条` : "我的表达资产"}
          </h2>
          {!selectedTopic && (
            <span className="text-[11px] text-echo-text/35">共 {filteredQuotes.length} 条</span>
          )}
        </div>
        <div className={`space-y-4 ${!selectedTopic ? "max-h-[480px] overflow-y-auto scrollbar-hide" : ""}`} style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
          {filteredQuotes.map((quote) => {
            const isFlipped = flippedCard === quote.id;
            const sourceBadge = getSourceBadge(quote.sourceType);

            return (
              <div
                key={quote.id}
                className="glass-card rounded-card overflow-hidden animate-spring"
                style={{ perspective: "1000px" }}
              >
                {/* Card Front */}
                <div
                  className={`relative p-5 cursor-pointer transition-transform duration-500 ${
                    isFlipped ? "rotateY-180" : ""
                  }`}
                  style={{ transformStyle: "preserve-3d" }}
                  onClick={() => setFlippedCard(isFlipped ? null : quote.id)}
                >
                  {/* Source badge */}
                  <div className="flex items-center gap-1.5 mb-3">
                    <span
                      className="px-2.5 py-1 rounded-full text-[11px] font-medium flex items-center gap-1"
                      style={{ background: sourceBadge.color }}
                    >
                      <span>{sourceBadge.emoji}</span>
                      {sourceBadge.text}
                    </span>
                    {quote.appliedCount > 0 && (
                      <span className="text-[11px] text-echo-text/40">已化用 {quote.appliedCount} 次</span>
                    )}
                  </div>

                  {/* Content */}
                  <p className="text-echo-text font-medium text-[15px] leading-relaxed mb-4">
                    "{quote.content}"
                  </p>

                  {/* Source */}
                  <p className="text-echo-text/40 text-[12px] mb-4">{quote.source}</p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {quote.tags.map((tag, i) => (
                      <span
                        key={i}
                        className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-white/30 text-echo-text/60 flex items-center gap-1"
                      >
                        <span>{tag.emoji}</span>
                        {tag.name}
                      </span>
                    ))}
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between">
                    <button className="flex items-center gap-1 text-[12px] text-echo-text/45 font-medium">
                      <Info size={12} strokeWidth={2} />
                      查看详情
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUseQuote(quote);
                      }}
                      className="px-4 py-2 rounded-full text-[13px] font-medium bg-gradient-to-r from-[#FFF3D8] to-[#F2EAFF] text-echo-text animate-spring"
                    >
                      ✨ 练练这句话
                    </button>
                  </div>
                </div>

                {/* Card Back */}
                {isFlipped && (
                  <div className="px-5 pb-5 animate-fade-slide">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4 pt-2 border-t border-echo-text/5">
                      <span className="text-[14px] font-bold text-echo-text">📖 来源详情</span>
                      <button
                        onClick={() => setFlippedCard(null)}
                        className="text-[12px] text-echo-text/40"
                      >
                        返回
                      </button>
                    </div>

                    {/* Source info */}
                    <div className="mb-5">
                      <p className="text-echo-text font-medium text-[15px] mb-1">{quote.source}</p>
                      {quote.sourceDetail && (
                        <p className="text-echo-text/40 text-[12px]">{quote.sourceDetail}</p>
                      )}
                    </div>

                    {/* Why */}
                    {quote.explanation && (
                      <div className="mb-5">
                        <h3 className="text-[13px] font-medium text-echo-text/70 mb-2 flex items-center gap-1">
                          <SparklesIcon size={12} strokeWidth={2} className="text-[#D4B86A]" />
                          这句话为什么容易打动人？
                        </h3>
                        <p className="text-echo-text/65 text-[13px] leading-relaxed">
                          {quote.explanation}
                        </p>
                      </div>
                    )}

                    {/* Scenarios */}
                    {quote.scenarios && (
                      <div className="mb-5">
                        <h3 className="text-[13px] font-medium text-echo-text/70 mb-2">常见使用场景</h3>
                        <div className="flex flex-wrap gap-2">
                          {quote.scenarios.map((s, i) => (
                            <span
                              key={i}
                              className="px-3 py-1.5 rounded-full text-[11px] font-medium bg-white/30 text-echo-text/60 flex items-center gap-1"
                            >
                              <span>✔</span>
                              {s}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Similar expressions */}
                    {quote.similar && (
                      <div className="mb-5">
                        <h3 className="text-[13px] font-medium text-echo-text/70 mb-2">相似表达</h3>
                        <div className="space-y-2">
                          {quote.similar.map((s, i) => (
                            <div key={i} className="px-3 py-2.5 rounded-2xl bg-white/25">
                              <p className="text-echo-text/65 text-[13px]">" {s.content} "</p>
                              <p className="text-echo-text/35 text-[11px] mt-1">{s.source}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Book recommendation */}
                    {quote.bookRecommendation && (
                      <div className="p-4 rounded-2xl bg-[#FFF3D8]/20">
                        <h3 className="text-[13px] font-medium text-echo-text/70 mb-2 flex items-center gap-1">
                          <BookOpen size={12} strokeWidth={2} className="text-[#D4B86A]" />
                          观察一下
                        </h3>
                        <p className="text-echo-text/60 text-[12px] leading-relaxed">
                          {quote.bookRecommendation}
                        </p>
                      </div>
                    )}

                    {/* Use button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUseQuote(quote);
                      }}
                      className="w-full mt-5 py-3 rounded-full text-[14px] font-medium bg-gradient-to-r from-[#FFF3D8] to-[#F2EAFF] text-echo-text animate-spring"
                    >
                      ✨ 练练这句话
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Recent Applied Records */}
      <section className="px-5 pb-5">
        <h2 className="text-[17px] font-bold text-echo-text mb-3">🌱 最近真正用出来的表达</h2>
        <div className="glass-card rounded-card p-5">
          {appliedRecords.map((record, i) => (
            <div key={i} className="flex gap-4 mb-4 last:mb-0">
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-[14px]">
                  {["🌱", "🌊", "🌸"][i]}
                </div>
                {i < appliedRecords.length - 1 && (
                  <div className="w-0.5 h-6 bg-echo-text/5 mt-1" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-[11px] text-echo-text/40 mb-0.5">{record.date}</p>
                <p className="text-echo-text/60 text-[12px] mb-1">{record.context}</p>
                <p className="text-echo-text font-medium text-[13px]">"{record.content}"</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <section className="px-5 pb-20">
        <p className="text-center text-echo-text/30 text-[12px] leading-relaxed">
          真正属于你的表达，不是记住了一句话，<br />
          而是忘记出处以后，它依然会自然地从你口中说出来。
        </p>
      </section>

      {/* Toast Notification */}
      {showToast && (
        <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
          <div className="glass-card rounded-card px-6 py-4 flex items-center gap-3 animate-fade-slide pointer-events-auto">
            <div className="w-8 h-8 rounded-full bg-[#DDF6E8]/30 flex items-center justify-center">
              <span>🌿</span>
            </div>
            <div>
              <p className="text-echo-text font-medium text-[14px]">已进入对应练习</p>
              <p className="text-echo-text/40 text-[11px]">Demo Mode</p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}